import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import Clickable from "@insite/mobius/Clickable";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner/LoadingSpinner";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import activateQuoteTerm from "@tKgpco/Handlers/Terms/ActivateTerms";
import removeTerm from "@tKgpco/Handlers/Terms/RemoveTerm";
import selectAllTerms from "@tKgpco/Handlers/Terms/SelectAllTerms";
import selectTerms from "@tKgpco/Handlers/Terms/SelectTerms";
import setTermSearchFilter from "@tKgpco/Handlers/Terms/SetTermSearchFilter";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    removeTerm,
    selectTerms,
    selectAllTerms,
    setTermSearchFilter,
    activateQuoteTerm,
};

const mapStateToProps = (state: CustomApplicationState) => ({
    isLoading: state.KGPState.customTerms.isLoading,
    terms: state.KGPState.customTerms.quoteTerms,
    selectedTermIds: state.KGPState.customTerms.selectedTermIds,
    isAllTermSelected: state.KGPState.customTerms.isAllTermSelected,
});

interface TermsTableStyles {
    wrapper?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    dataTable?: DataTableProps;
    checkboxHeader?: DataTableHeaderProps;
    editHeader?: DataTableHeaderProps;
    nameHeader?: DataTableHeaderProps;
    codeHeader?: DataTableHeaderProps;
    enabledHeader?: DataTableHeaderProps;
    descriptionHeader?: DataTableHeaderProps;
    checkboxCells?: DataTableCellProps;
    editCells?: DataTableCellProps;
    nameCells?: DataTableCellProps;
    codeCells?: DataTableCellProps;
    enabledCells?: DataTableCellProps;
    descriptionCells?: DataTableCellProps;
    rowCheckbox?: CheckboxPresentationProps;
}

const styles: TermsTableStyles = {
    wrapper: {
        css: css`
            overflow: auto;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        variant: "h4",
    },
    checkboxHeader: {
        tight: true,
        alignX: "center",
    },
    editHeader: {
        tight: true,
        alignX: "center",
    },
    enabledHeader: {
        tight: true,
        alignX: "left",
    },
    nameHeader: {
        tight: true,
        alignX: "left",
    },
    codeHeader: {
        tight: true,
        alignX: "left",
    },
    rowCheckbox: {
        css: css`
            border: 1px solid #a0a3ab;
        `
    },
    descriptionCells : {
        css: css`
            width: 52%;
            span {
                display: inline-block;
                text-wrap: wrap;       
            }
        `
    }
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const TermsTable: React.FC<Props> = ({
    id,
    isLoading,
    terms,
    selectedTermIds,
    isAllTermSelected,
    removeTerm,
    selectTerms,
    selectAllTerms,
    setTermSearchFilter,
    activateQuoteTerm,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const toasterContext = useContext(ToasterContext);
    const checkboxChangeHandler = (value: boolean, id?: string) => {
        if (id) {
            selectTerms({ ids: [id] });
        } else {
            selectAllTerms({ isAllTermSelected: value});
        }        
    };

    const handleDelete = (id: string) => {
        removeTerm({
            id,
            onSuccess: () => {
                setIsSubmitting(false);
                setTermSearchFilter({ key: "selectFilter", value: "" })
                setTermSearchFilter({ key: "selectFilterText", value: "" })
                toasterContext.addToast({
                    messageType: "success",
                    body: translate("Term removed successfully."),
                    timeoutLength: 4000,
                });
            },
        });
    };

    const handleActivate = (id: string) => {
        activateQuoteTerm({
            id,
            onSuccess: () => {
                setTermSearchFilter({ key: "selectFilter", value: "" })
                setTermSearchFilter({ key: "selectFilterText", value: "" })
                toasterContext.addToast({
                    messageType: "success",
                    body: translate("Term Activated successfully."),
                    timeoutLength: 4000,
                });
            },
        });
    };

    useEffect(() => {
        let ids: string[] = [];
        if(isAllTermSelected) {
            for (const row of terms!) {
                if (!selectedTermIds[row.id]) {
                    ids.push(row.id);
                }
            }
        } else {
            ids = Object.keys(selectedTermIds);
        }
        selectTerms({ ids });
    },[isAllTermSelected, terms]);

    if (isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    if (!terms) {
        return null;
    }

    if (terms.length === 0) {
        return (
            <StyledWrapper {...styles.noResultsContainer}>
                <Typography as="p" {...styles.noResultsText} data-test-selector="termsTable_noOrdersFound">
                    {translate("No results found")}
                </Typography>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <DataTable>
                <DataTableHead>
                    <DataTableHeader {...styles.checkboxHeader}>
                        <Checkbox
                            {...styles.rowCheckbox}
                            checked={isAllTermSelected}
                            onChange={(e, value) => checkboxChangeHandler(value)}
                        />
                    </DataTableHeader>
                    <DataTableHeader {...styles.editHeader}>{translate("Edit")}</DataTableHeader>
                    <DataTableHeader {...styles.codeHeader}>{translate("Code")}</DataTableHeader>
                    <DataTableHeader {...styles.nameHeader}>{translate("Vendor Item Id")}</DataTableHeader>
                    <DataTableHeader {...styles.nameHeader}>{translate("Type")}</DataTableHeader>
                    <DataTableHeader {...styles.enabledHeader}>{translate("Estimate")}</DataTableHeader>
                    <DataTableHeader {...styles.enabledHeader}>{translate("Default")}</DataTableHeader>
                    <DataTableHeader {...styles.enabledHeader}>{translate("Source")}</DataTableHeader>
                    <DataTableHeader {...styles.descriptionHeader}>{translate("Description")}</DataTableHeader>
                    <DataTableHeader {...styles.editHeader}>
                        <VisuallyHidden>{translate("Delete")}</VisuallyHidden>
                    </DataTableHeader>
                </DataTableHead>
                <DataTableBody>
                    {terms.map(term => (
                        <DataTableRow key={term.id}>
                            <DataTableCell {...styles.checkboxCells}>
                                <Checkbox
                                    {...styles.rowCheckbox}
                                    checked={selectedTermIds[term.id]}
                                    onChange={(e, value) => checkboxChangeHandler(value, term.id)}
                                />
                            </DataTableCell>
                            <DataTableCell {...styles.editCells}>
                                {!term.isActive ? null : <Link href={`/MyAccount/Terms/EditTerms?TermId=${term.id}`}>
                                    <Icon src="Edit2" size={20} />
                                </Link>}
                            </DataTableCell>
                            <DataTableCell {...styles.codeCells}>{term.termsCode}</DataTableCell>
                            <DataTableCell {...styles.codeCells}>{term.vendor}</DataTableCell>
                            <DataTableCell {...styles.codeCells}>{term.type}</DataTableCell>
                            <DataTableCell {...styles.enabledCells}>
                                {term.estimate ? "true" : "false"}
                            </DataTableCell>
                            <DataTableCell {...styles.enabledCells}>
                                {term.default ? "true" : "false"}
                            </DataTableCell>
                            <DataTableCell {...styles.codeCells}>{term.source}</DataTableCell>
                            <DataTableCell {...styles.descriptionCells}>{term.description}</DataTableCell>
                            <DataTableCell {...styles.editCells}>
                                {!term.isActive ? <Button
                                        variant="primary"
                                        onClick={() => handleActivate(term.id)}
                                    >
                                        {translate("Activate")}
                                    </Button> : <Clickable onClick={() => handleDelete(term.id)}>
                                    <Icon src="Trash2" size={20} />
                                </Clickable>}
                            </DataTableCell>
                        </DataTableRow>
                    ))}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

const termsTable: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(TermsTable),
    definition: {
        group: "Basic",
    },
};

export default termsTable;
